"use client";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { OfferItem } from "./OfferItem";
import type { OffersOutput } from "@/interfaces/offers";

interface OffersListProps {
  sectionId: string;
  offers: OffersOutput[];
  onAddOffer: (sectionId: string) => void;
  onToggleVisibility: (offerId: string) => void;
  onDelete: (offerId: string) => void;
  onMoveOffer?: (offerId: string, direction: "up" | "down") => void;
}

export function OffersList({
  sectionId,
  offers,
  onAddOffer,
  onToggleVisibility,
  onDelete,
  onMoveOffer,
}: OffersListProps) {
  // Ordenar ofertas por posição
  const sortedOffers = [...offers]
    .filter((o) => o.sectionId === sectionId)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div className="relative">
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-medium">Produtos na Seção</h4>

        {/* CTA topo (desktop prioritário) */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAddOffer(sectionId)}
          className="hidden sm:flex items-center gap-1"
        >
          <Plus className="h-3 w-3 mr-1" />
          Adicionar Produto
        </Button>
      </div>

      {sortedOffers.length === 0 ? (
        <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium mb-2">
              Nenhum produto nesta seção
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Adicione produtos ou ofertas para começar a organizar seu catálogo
            </p>
            <Button
              variant="default"
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              onClick={() => onAddOffer(sectionId)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Primeiro Produto
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedOffers.map((offer) => (
              <OfferItem
                key={offer.id}
                offer={offer}
                onToggleVisibility={onToggleVisibility}
                onDelete={onDelete}
                onMoveUp={
                  onMoveOffer && offer.position !== 1
                    ? () => onMoveOffer(offer.id, "up")
                    : undefined
                }
                onMoveDown={
                  onMoveOffer && offer.position !== sortedOffers.length
                    ? () => onMoveOffer(offer.id, "down")
                    : undefined
                }
              />
            ))}
          </div>

          {/* CTA fim de lista (mobile-first) */}
          <div className="mt-4 flex sm:hidden">
            <Button
              onClick={() => onAddOffer(sectionId)}
              className="w-full bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Produto
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
